<script setup>
import {ref} from 'vue';
import DefaultButton from '@/components/global-themed/buttons/DefaultButton.vue';
import BaseModal from '@/components/global-themed/BaseModal.vue';
import {useDeleteBanner} from '@/composables/admin/useAdminBanners.js';
import {errorToast, successToast} from '@/composables/useToasts';

const props = defineProps({
    banner: {
        type: Object,
        required: true,
    },
    buttonClass: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['deleted']);

const showModal = ref(false);
const {mutate: deleteBanner, isPending: isDeleting} = useDeleteBanner();

function closeModal() {
    showModal.value = false;
}

function confirmDelete() {
    deleteBanner(props.banner.id, {
        onSuccess: () => {
            closeModal();
            successToast('Banner deleted successfully.');
            emit('deleted');
        },
        onError: () => {
            errorToast('Something went wrong deleting the banner.');
        },
    });
}
</script>

<template>
  <DefaultButton
    variant="danger"
    size="small"
    :additional-classes="buttonClass"
    @click="showModal = true"
  >
    <span class="tw-font-semibold tw-antialiased">Delete</span>
  </DefaultButton>

  <Teleport to=".tailwind">
    <BaseModal
      v-if="showModal"
      modal-class="tw-max-w-96"
      @close="closeModal"
    >
      <div>
        <h2 class="tw-text-2xl tw-text-center tw-font-bold">
          Delete Banner?
        </h2>

        <p class="tw-my-6 tw-text-center">
          Are you sure you want to delete "{{ banner.title }}"? This cannot be undone.
        </p>

        <div class="tw-flex tw-justify-center tw-gap-2">
          <DefaultButton
            variant="primary-inverted"
            size="small"
            :disabled="isDeleting"
            @click="closeModal"
          >
            Cancel
          </DefaultButton>

          <DefaultButton
            variant="danger"
            size="small"
            :loading="isDeleting"
            :disabled="isDeleting"
            @click="confirmDelete"
          >
            {{ isDeleting ? 'Deleting...' : 'Delete Banner' }}
          </DefaultButton>
        </div>
      </div>
    </BaseModal>
  </Teleport>
</template>
